import { ApolloClient, ApolloProvider, ApolloLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { removeTypenameFromVariables } from '@apollo/client/link/remove-typename';
import { InMemoryCache } from '@apollo/client/cache';
import { createUploadLink } from 'apollo-upload-client';

import { useAuth } from '../contexts/auth-context';

import { captureMessage } from '../lib/error-handler';

import ENV from '../constants/Env';

const OnwardApolloProvider = ({ children }) => {
  const auth = useAuth();

  const authLink = setContext((request, previousContext) => {
    const authHeaders = auth.getAuthRequestHeaders();
    return {
      headers: authHeaders,
    };
  });

  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (networkError?.statusCode === 401) {
      auth.logout(apolloClient);
      return;
    }

    if (graphQLErrors) {
      graphQLErrors.forEach(graphQLError => {
        const isString = typeof graphQLError === 'string';
        const msg = isString ? graphQLError : graphQLError.message;
        const locations = isString ? undefined : graphQLError.locations;
        const path = isString ? undefined : graphQLError.path;
        captureMessage(
          `[GraphQL error]: Message: ${msg}, Location: ${locations}, Path: ${path}`
        );
      });
    }

    if (networkError) {
      captureMessage(`[Network error]: ${networkError}`);
    }
  });

  const uploadLink = createUploadLink({
    uri: `${ENV.apiUrl}/graphql`,
    credentials: 'same-origin',
  });

  const removeTypenameLink = removeTypenameFromVariables();

  const apolloClient = new ApolloClient({
    cache: new InMemoryCache(),
    link: ApolloLink.from([
      removeTypenameLink,
      errorLink,
      authLink,
      uploadLink,
    ]),
  });
  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
};

export default OnwardApolloProvider;
